import { z } from 'zod';
import { isValidIban, normalizeIban } from './reference.ts';

/**
 * Zod-skeemat jaetaan frontendin ja backendin kesken: sama skeema tarkistaa lomakkeen
 * ja API-kutsun. Virheviestit ovat käännösavaimia (errors.*), jotka frontend kääntää.
 */

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email({ error: 'errors.email' }));

// ---------------------------------------------------------------------------
// Kirjautuminen
// ---------------------------------------------------------------------------

export const loginLinkRequestSchema = z.object({ email: emailSchema });
export type LoginLinkRequest = z.infer<typeof loginLinkRequestSchema>;

export const verifyLoginRequestSchema = z.object({ token: z.string().min(20).max(200) });
export type VerifyLoginRequest = z.infer<typeof verifyLoginRequestSchema>;

/** Sähköpostin vaihto: uuteen osoitteeseen lähtee vahvistuslinkki. */
export const emailChangeRequestSchema = z.object({ email: emailSchema });
export type EmailChangeRequest = z.infer<typeof emailChangeRequestSchema>;

export const emailChangeConfirmSchema = z.object({ token: z.string().min(20).max(200) });
export type EmailChangeConfirm = z.infer<typeof emailChangeConfirmSchema>;

export const devLoginRoles = ['landlord', 'tenant', 'formerTenant', 'admin'] as const;
export const devLoginRequestSchema = z.object({ as: z.enum(devLoginRoles) });
export type DevLoginRequest = z.infer<typeof devLoginRequestSchema>;

/** Esittelyversion kirjautuminen esimerkkikäyttäjänä. */
export const demoLoginRoles = ['landlord', 'tenant'] as const;
export const demoLoginRequestSchema = z.object({ as: z.enum(demoLoginRoles) });
export type DemoLoginRequest = z.infer<typeof demoLoginRequestSchema>;

export const localeSchema = z.enum(['fi', 'sv', 'en']);
export type Locale = z.infer<typeof localeSchema>;

export const meSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  name: z.string().nullable(),
  phone: z.string().nullable(),
  locale: localeSchema,
  isAdmin: z.boolean(),
  /** Mitkä vuokranantajan yhteystiedot näkyvät hänen vuokralaisilleen (nimi näkyy aina). */
  showEmailToTenants: z.boolean(),
  showPhoneToTenants: z.boolean(),
  /** Tilinumero Vuokrantin laskuille. */
  iban: z.string().nullable(),
  /** Aloitusohje suljettu (piilotettu tai kaikki askeleet tehty): ei näytetä enää. */
  onboardingClosed: z.boolean(),
  roles: z.object({
    landlord: z.boolean(),
    tenant: z.boolean(),
  }),
});
export type Me = z.infer<typeof meSchema>;

// ---------------------------------------------------------------------------
// Yhteiset kenttätyypit
// ---------------------------------------------------------------------------

const optionalText = z
  .string()
  .trim()
  .max(200)
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

const optionalPhone = z
  .string()
  .trim()
  .regex(/^(\+?[0-9 ()-]{5,20})?$/, { error: 'errors.phone' })
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .refine((v) => v === '' || z.email().safeParse(v).success, { error: 'errors.email' })
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

/** Omat tiedot (käyttäjäasetukset). */
/** Tilinumero: välilyönnit pois, isot kirjaimet, tarkiste. Tyhjä = ei tilinumeroa. */
export const ibanSchema = z
  .string()
  .trim()
  .transform((v) => normalizeIban(v))
  .refine((v) => v === '' || isValidIban(v), { error: 'errors.iban' })
  .transform((v) => (v === '' ? null : v));

export const meUpdateSchema = z.object({
  name: z
    .string()
    .trim()
    .max(100)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  phone: optionalPhone,
  showEmailToTenants: z.boolean().optional(),
  showPhoneToTenants: z.boolean().optional(),
  iban: ibanSchema.nullable().optional(),
  /** Aloitusohjeen voi vain sulkea, ei avata uudelleen. */
  onboardingClosed: z.literal(true).optional(),
});
export type MeUpdate = z.input<typeof meUpdateSchema>;

// ---------------------------------------------------------------------------
// Kohteet
// ---------------------------------------------------------------------------

export const propertyTypes = ['apartment', 'house', 'commercial'] as const;
export const propertyTypeSchema = z.enum(propertyTypes);
export type PropertyType = z.infer<typeof propertyTypeSchema>;

/** Talotyyppi on eri asia kuin omistusmuoto: osakehuoneisto voi olla kerros- tai rivitalossa. */
export const buildingTypes = [
  'apartment_building', // kerrostalo
  'row_house', // rivitalo
  'semi_detached', // paritalo
  'detached', // omakotitalo
  'other',
] as const;
export const buildingTypeSchema = z.enum(buildingTypes);
export type BuildingType = z.infer<typeof buildingTypeSchema>;

/** Asunnon lisäyksen vaiheet 1 (perustiedot) ja 2 (omistus). */
export const propertyBasicsSchema = z.object({
  type: propertyTypeSchema,
  buildingType: buildingTypeSchema.nullable().optional(),
  name: optionalText,
  streetAddress: z.string().trim().min(3, { error: 'errors.required' }).max(200),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}$/, { error: 'errors.postalCode' }),
  city: z.string().trim().min(2, { error: 'errors.required' }).max(100),
  housingCompany: optionalText,
  housingCompanyBusinessId: z
    .string()
    .trim()
    .regex(/^(\d{7}-\d)?$/, { error: 'errors.businessId' })
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  areaM2: z.number({ error: 'errors.number' }).positive().max(99999).nullable().optional(),
  roomLayout: optionalText,
});

/** Lisätiedot, joita muokataan yleiskatsauksessa. */
export const propertyDetailsSchema = z.object({
  floor: z.number({ error: 'errors.number' }).int().min(-5).max(99).nullable().optional(),
  buildYear: z
    .number({ error: 'errors.number' })
    .int({ error: 'errors.year' })
    .min(1700, { error: 'errors.year' })
    .max(2100, { error: 'errors.year' })
    .nullable()
    .optional(),
});

export const propertyOwnershipSchema = z.object({
  /** Oma omistusosuus prosentteina, esim. 50 tai 33.33 */
  ownershipPercent: z
    .number({ error: 'errors.number' })
    .min(0.01, { error: 'errors.ownership' })
    .max(100, { error: 'errors.ownership' }),
});

export const propertyCreateSchema = propertyBasicsSchema.extend(propertyOwnershipSchema.shape);
export type PropertyCreate = z.infer<typeof propertyCreateSchema>;

/** Muokkaus: vain lähetetyt kentät muuttuvat. */
export const propertyUpdateSchema = propertyBasicsSchema
  .extend(propertyDetailsSchema.shape)
  .extend(propertyOwnershipSchema.shape)
  .partial();
export type PropertyUpdate = z.input<typeof propertyUpdateSchema>;

/** Prosentit peruspisteiksi: 33.33 → 3333 */
export function percentToBp(percent: number): number {
  return Math.round(percent * 100);
}

/**
 * Asuntokohtaiset asetukset. Koskevat vain palveluun kutsuttuja vuokralaisia; oletukset
 * vastaavat aiempaa toimintaa.
 */
export const propertySettingsSchema = z.object({
  /** Vuokralainen tekee muutto- ja lopputarkastuksen (pois: vuokranantaja tekee itse). */
  tenantInspections: z.boolean(),
  /** Vuokralaisen "Maksoin"-ilmoitus. */
  tenantPaymentReports: z.boolean(),
  /** Laskut näkyvät vuokralaiselle. */
  tenantInvoices: z.boolean(),
  /** Vuokralainen voi aloittaa viestejä ja vikailmoituksia ja vastata. */
  tenantMessages: z.boolean(),
  /** Uusi huoltokirjan merkintä jaetaan vuokralaiselle oletuksena. */
  maintenanceShared: z.boolean(),
});
export type PropertySettings = z.infer<typeof propertySettingsSchema>;

export const propertySchema = z.object({
  id: z.uuid(),
  type: propertyTypeSchema,
  buildingType: buildingTypeSchema.nullable(),
  name: z.string().nullable(),
  streetAddress: z.string(),
  postalCode: z.string(),
  city: z.string(),
  housingCompany: z.string().nullable(),
  housingCompanyBusinessId: z.string().nullable(),
  areaM2: z.number().nullable(),
  roomLayout: z.string().nullable(),
  floor: z.number().nullable(),
  buildYear: z.number().nullable(),
  myRole: z.enum(['owner', 'tenant']),
  myOwnershipBp: z.number().nullable(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  /** Voimassa olevan vuokrasuhteen tiivistelmä listoja varten (vain omistajalle). */
  currentLease: z.object({ rentCents: z.number(), tenantNames: z.array(z.string()) }).nullable(),
  /**
   * Vuokralainen on muuttanut pois (päättymispäivä ohi), mutta vuokrasuhdetta ei ole suljettu
   * (vakuus palauttamatta). Näytetään "Päättäminen kesken" eikä "Vapaa". Vain omistajalle.
   */
  movedOutLease: z.object({ endDate: z.string(), tenantNames: z.array(z.string()) }).nullable(),
  settings: propertySettingsSchema,
});
export type Property = z.infer<typeof propertySchema>;

export const propertyListSchema = z.object({ properties: z.array(propertySchema) });

/** Vuokranantajan yhteystiedot vuokralaiselle: sähköposti ja puhelin vain, jos hän on sallinut. */
export const landlordContactSchema = z.object({
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
});
export type LandlordContact = z.infer<typeof landlordContactSchema>;

// ---------------------------------------------------------------------------
// Taloyhtiön yhteystiedot
// ---------------------------------------------------------------------------

export const contactRoles = [
  'manager', // isännöitsijä
  'maintenance', // huoltoyhtiö
  'caretaker', // talonmies
  'board_chair', // hallituksen puheenjohtaja
  'secretary', // sihteeri
  'other',
] as const;
export const contactRoleSchema = z.enum(contactRoles);
export type ContactRole = z.infer<typeof contactRoleSchema>;

export const contactInputSchema = z
  .object({
    role: contactRoleSchema,
    roleLabel: optionalText,
    name: optionalText,
    phone: optionalPhone,
    email: optionalEmail,
    visibleToTenant: z.boolean(),
  })
  .refine((c) => c.role !== 'other' || Boolean(c.roleLabel), {
    error: 'errors.required',
    path: ['roleLabel'],
  })
  .refine((c) => Boolean(c.name || c.phone || c.email), {
    error: 'errors.contactEmpty',
    path: ['name'],
  });
export type ContactInput = z.input<typeof contactInputSchema>;

export const propertyContactSchema = z.object({
  id: z.uuid(),
  role: contactRoleSchema,
  roleLabel: z.string().nullable(),
  name: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  visibleToTenant: z.boolean(),
});
export type PropertyContact = z.infer<typeof propertyContactSchema>;

// ---------------------------------------------------------------------------
// Vuokrasuhteet
// ---------------------------------------------------------------------------

export const leaseStatusSchema = z.enum(['draft', 'active', 'ended']);
export const waterBillingSchema = z.enum(['included', 'per_person', 'metered']);
export const electricityBillingSchema = z.enum(['own_contract', 'included']);
export const petsPolicies = ['allowed', 'not_allowed', 'by_agreement'] as const;
export const petsPolicySchema = z.enum(petsPolicies);
export type PetsPolicy = z.infer<typeof petsPolicySchema>;

export const leasePartySchema = z.object({
  id: z.uuid(),
  role: z.enum(['tenant', 'resident']),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  /** Onko tämä osapuoli kirjautunut käyttäjä itse. */
  isMe: z.boolean(),
  /** Kutsun tila: none = ei kutsuttu (pelkkä nimi), invited = kutsuttu, joined = käyttää sovellusta. */
  access: z.enum(['none', 'invited', 'joined']),
});

export const terminationBys = ['tenant', 'landlord', 'mutual', 'fixed_term'] as const;
export const terminationBySchema = z.enum(terminationBys);
export type TerminationBy = z.infer<typeof terminationBySchema>;

export const depositDeductionKinds = ['damage', 'unpaid_rent', 'other'] as const;
export const depositDeductionKindSchema = z.enum(depositDeductionKinds);
export type DepositDeductionKind = z.infer<typeof depositDeductionKindSchema>;
export const moveOutCheckSchema = z.enum(['app', 'external', 'none']);
export type MoveOutCheck = z.infer<typeof moveOutCheckSchema>;

/** Vakuuden pidätys: syy, summa, selite ja kuvat lopputarkastuksesta. */
export const depositDeductionSchema = z.object({
  id: z.uuid(),
  kind: depositDeductionKindSchema,
  amountCents: z.number().int(),
  description: z.string(),
  photoIds: z.array(z.uuid()),
});
export type DepositDeduction = z.infer<typeof depositDeductionSchema>;

export const leaseSchema = z.object({
  id: z.uuid(),
  propertyId: z.uuid(),
  roomLabel: z.string().nullable(),
  status: leaseStatusSchema,
  startDate: z.string(),
  endDate: z.string().nullable(),
  rentCents: z.number(),
  dueDay: z.number(),
  depositCents: z.number(),
  waterBilling: waterBillingSchema,
  waterCents: z.number().nullable(),
  electricityBilling: electricityBillingSchema,
  rentIncreaseTerms: z.string().nullable(),
  signedOn: z.string().nullable(),
  signedMethod: z.enum(['paper', 'bank_id']).nullable(),
  /** Lemmikit sopimuksen mukaan; null = ei sovittu. */
  pets: petsPolicySchema.nullable(),
  /** app = muuttotarkastus tehdään sovelluksessa, external = tehty muualla. */
  moveInInspection: z.enum(['app', 'external']),
  /** Irtisanominen: kuka ja milloin. Viimeinen päivä on endDate. */
  terminatedBy: terminationBySchema.nullable(),
  noticeOn: z.string().nullable(),
  depositReturnedCents: z.number().nullable(),
  depositReturnedOn: z.string().nullable(),
  depositNote: z.string().nullable(),
  /** Vakuuden pidätykset (päättyneelle vuokrasuhteelle). */
  depositDeductions: z.array(depositDeductionSchema),
  /** Lopputarkastus: sovelluksessa, muualla vai ei tehty (kirjataan sulkiessa). */
  moveOutCheck: moveOutCheckSchema.nullable(),
  /** Avoimena olevien laskujen summa (myös erääntymättömät): vakuudesta katettava enintään. */
  openInvoicesCents: z.number(),
  /** Erääntyneet maksamattomat (eräpäivä ohi, ei "Maksettu"-ilmoitusta odottamassa). */
  overdueCents: z.number(),
  /** Onko vuokrasuhteella laskuja palvelussa (näkyvät vuokralaiselle vain asetuksen mukaan). */
  hasInvoices: z.boolean(),
  /** Vuokrasuhteeseen liitetty sopimustiedosto. */
  contractFile: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  /** Asunnon asetus: tekeekö vuokralainen tarkastukset. */
  tenantInspections: z.boolean(),
  endedAt: z.string().nullable(),
  parties: z.array(leasePartySchema),
});
export type Lease = z.infer<typeof leaseSchema>;

/** Vuokrasuhteen lisäyksen vuokralainen: nimi, sähköposti ja kutsutaanko sovellukseen. */
export const leaseTenantInputSchema = z
  .object({
    name: z.string().trim().min(1, { error: 'errors.required' }).max(100),
    email: optionalEmail,
    invite: z.boolean(),
  })
  .refine((v) => !v.invite || Boolean(v.email), {
    error: 'errors.inviteEmail',
    path: ['email'],
  });
export type LeaseTenantInput = z.input<typeof leaseTenantInputSchema>;

const leaseDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { error: 'errors.date' });

/**
 * Olemassa olevan vuokrasuhteen kirjaus: sopimus on jo allekirjoitettu paperilla, joten
 * vuokrasuhde on heti voimassa. Sähköinen allekirjoitus lisätään myöhemmin.
 */
export const leaseCreateSchema = z
  .object({
    startDate: leaseDate,
    endDate: leaseDate.nullable(),
    rentCents: z.number().int().min(1, { error: 'errors.amount' }).max(10_000_000),
    dueDay: z.number().int().min(1).max(31),
    depositCents: z.number().int().min(0).max(10_000_000),
    waterBilling: waterBillingSchema,
    waterCents: z.number().int().min(0).max(100_000).nullable(),
    electricityBilling: electricityBillingSchema,
    rentIncreaseTerms: z
      .string()
      .trim()
      .max(500)
      .transform((v) => (v === '' ? null : v))
      .nullable(),
    petsAllowed: z.boolean(),
    moveInInspection: z.enum(['app', 'external']),
    signedOn: leaseDate,
    tenants: z.array(leaseTenantInputSchema).min(1).max(6),
  })
  .refine((v) => !v.endDate || v.endDate >= v.startDate, {
    error: 'errors.endBeforeStart',
    path: ['endDate'],
  })
  .refine((v) => v.waterBilling !== 'per_person' || v.waterCents !== null, {
    error: 'errors.required',
    path: ['waterCents'],
  });
export type LeaseCreate = z.input<typeof leaseCreateSchema>;

/**
 * Voimassa olevan vuokrasuhteen ehtojen muutos. Vakuutta ja alkamispäivää ei voi muuttaa.
 * Perustelu on pakollinen ja tallentuu asunnon lokiin.
 */
export const leaseTermsUpdateSchema = z
  .object({
    rentCents: z.number().int().min(1, { error: 'errors.amount' }).max(10_000_000),
    dueDay: z.number().int().min(1).max(31),
    waterBilling: waterBillingSchema,
    waterCents: z.number().int().min(0).max(100_000).nullable(),
    electricityBilling: electricityBillingSchema,
    rentIncreaseTerms: z.string().trim().max(500).nullable(),
    endDate: leaseDate.nullable(),
    reason: z.string().trim().min(3, { error: 'errors.reason' }).max(1000),
  })
  .refine((v) => v.waterBilling !== 'per_person' || v.waterCents !== null, {
    error: 'errors.required',
    path: ['waterCents'],
  });
export type LeaseTermsUpdate = z.input<typeof leaseTermsUpdateSchema>;

/** Asunnon lokin tapahtuma. Teksti muodostetaan käyttöliittymässä tyypin ja tietojen mukaan. */
export const propertyEventSchema = z.object({
  id: z.uuid(),
  type: z.string(),
  details: z.record(z.string(), z.unknown()),
  reason: z.string().nullable(),
  actorName: z.string().nullable(),
  leaseId: z.uuid().nullable(),
  createdAt: z.string(),
});
export type PropertyEvent = z.infer<typeof propertyEventSchema>;

export const terminateLeaseSchema = z.object({
  by: terminationBySchema,
  noticeOn: leaseDate,
  endDate: leaseDate,
});
export type TerminateLease = z.input<typeof terminateLeaseSchema>;

export const depositDeductionInputSchema = z.object({
  kind: depositDeductionKindSchema,
  amountCents: z.number().int().min(1).max(10_000_000),
  description: z.string().trim().min(1, { error: 'errors.required' }).max(1000),
  photoIds: z.array(z.uuid()).max(20),
});
export type DepositDeductionInput = z.input<typeof depositDeductionInputSchema>;

/** Sulkeminen: palautettu summa + pidätykset = vakuus (tarkistetaan tietokannassa). */
export const closeLeaseSchema = z.object({
  depositReturnedCents: z.number().int().min(0).max(10_000_000),
  depositReturnedOn: leaseDate,
  note: z.string().trim().max(1000).optional(),
  moveOutCheck: moveOutCheckSchema,
  deductions: z.array(depositDeductionInputSchema).max(20),
});
export type CloseLease = z.input<typeof closeLeaseSchema>;
export type LeaseParty = z.infer<typeof leasePartySchema>;

/** Asunnossa asuvat muut kuin sopimuksen vuokralaiset, ja lemmikit. Muokattavissa aina. */
export const occupantKindSchema = z.enum(['person', 'pet']);
export type OccupantKind = z.infer<typeof occupantKindSchema>;

export const occupantInputSchema = z.object({
  kind: occupantKindSchema,
  name: z.string().trim().min(1, { error: 'errors.required' }).max(100),
  species: z
    .string()
    .trim()
    .max(60)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
});
export type OccupantInput = z.input<typeof occupantInputSchema>;

export const occupantSchema = z.object({
  id: z.uuid(),
  kind: occupantKindSchema,
  name: z.string(),
  species: z.string().nullable(),
  addedByName: z.string().nullable(),
  createdAt: z.string(),
});
export type Occupant = z.infer<typeof occupantSchema>;

// ---------------------------------------------------------------------------
// Tiedostot
// ---------------------------------------------------------------------------

/** Kansiot näyttöjärjestyksessä. */
export const fileFolders = [
  'contract',
  'housing_company',
  'invoice',
  'receipt',
  'photo',
  'other',
] as const;
export const fileFolderSchema = z.enum(fileFolders);
export type FileFolder = z.infer<typeof fileFolderSchema>;

/** Sallitut tiedostotyypit. Tyyppi tarkistetaan tiedoston sisällöstä, ei nimestä. */
export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;
export const MAX_FILE_BYTES = 15 * 1024 * 1024;

export const fileSchema = z.object({
  id: z.uuid(),
  folder: fileFolderSchema,
  sharedWithTenant: z.boolean(),
  name: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  createdAt: z.string(),
  /** Huoltokirjan tapahtuma, jonka liite tiedosto on (näkyy myös Tiedostoissa). */
  maintenanceEntry: z.object({ id: z.uuid(), title: z.string() }).nullable(),
});
export type StoredFile = z.infer<typeof fileSchema>;

export const fileUpdateSchema = z.object({
  folder: fileFolderSchema.optional(),
  sharedWithTenant: z.boolean().optional(),
  name: z.string().trim().min(1).max(200).optional(),
});
export type FileUpdate = z.infer<typeof fileUpdateSchema>;

// ---------------------------------------------------------------------------
// Muuttotarkastukset
// ---------------------------------------------------------------------------

export const inspectionTypeSchema = z.enum(['move_in', 'move_out']);
export const inspectionStatusSchema = z.enum(['draft', 'submitted', 'approved']);
export type InspectionStatus = z.infer<typeof inspectionStatusSchema>;

/** Kuviin sallitaan vain kuvatyypit. */
export const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

export const inspectionPhotoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  capturedAt: z.string().nullable(),
  uploadedAt: z.string(),
  uploadedByMe: z.boolean(),
});

export const inspectionRoomSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  note: z.string().nullable(),
  photos: z.array(inspectionPhotoSchema),
});

export const inspectionNoteSchema = z.object({
  id: z.uuid(),
  text: z.string(),
  authorName: z.string().nullable(),
  byMe: z.boolean(),
  createdAt: z.string(),
});

export const inspectionSchema = z.object({
  id: z.uuid(),
  leaseId: z.uuid(),
  type: inspectionTypeSchema,
  status: inspectionStatusSchema,
  dueDate: z.string().nullable(),
  submittedAt: z.string().nullable(),
  submittedByName: z.string().nullable(),
  approvedAt: z.string().nullable(),
  approvedByName: z.string().nullable(),
  returnComment: z.string().nullable(),
  /** Kirjautuneen käyttäjän suhde: omistaja vai vuokrasuhteen osapuoli. */
  myAccess: z.enum(['owner', 'party']),
  rooms: z.array(inspectionRoomSchema),
  notes: z.array(inspectionNoteSchema),
});
export type Inspection = z.infer<typeof inspectionSchema>;
export type InspectionRoom = z.infer<typeof inspectionRoomSchema>;
export type InspectionPhoto = z.infer<typeof inspectionPhotoSchema>;

export const roomInputSchema = z.object({
  name: z.string().trim().min(1, { error: 'errors.required' }).max(80).optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});
export const returnInspectionSchema = z.object({
  comment: z.string().trim().min(1, { error: 'errors.required' }).max(2000),
});
export const inspectionNoteInputSchema = z.object({
  text: z.string().trim().min(1, { error: 'errors.required' }).max(2000),
});

/** Uuden tarkastuksen oletushuoneet. */
export const DEFAULT_ROOMS = ['Eteinen', 'Olohuone', 'Makuuhuone', 'Keittiö', 'Kylpyhuone'];

/** Tarkastus lähetetään viimeistään näin monta päivää vuokrasuhteen alusta. */
export const INSPECTION_DAYS = 14;

// ---------------------------------------------------------------------------
// Huoltokirja
// ---------------------------------------------------------------------------

export const maintenanceKinds = [
  'renovation', // remontti
  'repair', // korjaus
  'purchase', // hankinta, esim. uusi jääkaappi
  'housing_company', // taloyhtiön työ, esim. putkiremontti
  'other',
] as const;
export const maintenanceKindSchema = z.enum(maintenanceKinds);
export type MaintenanceKind = z.infer<typeof maintenanceKindSchema>;

export const maintenancePerformers = ['self', 'company', 'housing_company'] as const;
export const maintenancePerformerSchema = z.enum(maintenancePerformers);
export type MaintenancePerformer = z.infer<typeof maintenancePerformerSchema>;

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { error: 'errors.date' });

/** Tapahtuman lisäys ja muokkaus. Hinta sentteinä; lomake muuntaa euroista. */
export const maintenanceInputSchema = z.object({
  happenedOn: isoDate,
  title: z.string().trim().min(1, { error: 'errors.required' }).max(200),
  kind: maintenanceKindSchema,
  performer: maintenancePerformerSchema.nullable(),
  priceCents: z.number().int().min(0).max(100_000_000).nullable(),
  warrantyUntil: isoDate.nullable(),
  notes: z
    .string()
    .trim()
    .max(4000)
    .transform((v) => (v === '' ? null : v))
    .nullable(),
  visibleToTenant: z.boolean(),
});
export type MaintenanceInput = z.input<typeof maintenanceInputSchema>;

export const maintenanceEntrySchema = z.object({
  id: z.uuid(),
  happenedOn: z.string(),
  title: z.string(),
  kind: maintenanceKindSchema,
  performer: maintenancePerformerSchema.nullable(),
  /** Vain omistajalle; vuokralaiselle aina null. */
  priceCents: z.number().nullable(),
  warrantyUntil: z.string().nullable(),
  notes: z.string().nullable(),
  visibleToTenant: z.boolean(),
  /** Liitteet; vuokralaiselle vain hänelle jaetut. */
  attachments: z.array(fileSchema),
  /** Kulu, joka merkinnästä on kirjattu (vain omistajalle). */
  expenseId: z.uuid().nullable(),
  createdAt: z.string(),
});
export type MaintenanceEntry = z.infer<typeof maintenanceEntrySchema>;

// ---------------------------------------------------------------------------
// Viestit ja vikailmoitukset
// ---------------------------------------------------------------------------

export const threadKindSchema = z.enum(['message', 'fault']);
export type ThreadKind = z.infer<typeof threadKindSchema>;
export const faultStatuses = ['new', 'in_progress', 'fixed'] as const;
export const faultStatusSchema = z.enum(faultStatuses);
export type FaultStatus = z.infer<typeof faultStatusSchema>;

const messageBody = z.string().trim().min(1, { error: 'errors.required' }).max(4000);

export const threadCreateSchema = z.object({
  kind: threadKindSchema,
  subject: z.string().trim().min(1, { error: 'errors.required' }).max(200),
  body: messageBody,
});
export type ThreadCreate = z.input<typeof threadCreateSchema>;

export const messageCreateSchema = z.object({ body: messageBody });
export const threadUpdateSchema = z.object({ status: faultStatusSchema });

export const threadSummarySchema = z.object({
  id: z.uuid(),
  leaseId: z.uuid(),
  propertyId: z.uuid(),
  propertyTitle: z.string(),
  kind: threadKindSchema,
  subject: z.string(),
  status: faultStatusSchema.nullable(),
  createdAt: z.string(),
  lastMessageAt: z.string(),
  lastMessage: z
    .object({ body: z.string(), authorName: z.string().nullable(), byMe: z.boolean() })
    .nullable(),
  messageCount: z.number(),
  unread: z.boolean(),
});
export type ThreadSummary = z.infer<typeof threadSummarySchema>;

export const messageSchema = z.object({
  id: z.uuid(),
  body: z.string(),
  authorName: z.string().nullable(),
  byMe: z.boolean(),
  createdAt: z.string(),
});
export type Message = z.infer<typeof messageSchema>;

export const threadSchema = threadSummarySchema.extend({
  /** Saako käyttäjä muuttaa vikailmoituksen tilaa (omistaja). */
  canSetStatus: z.boolean(),
  /** Voiko ketjuun kirjoittaa: ei, kun vuokrasuhde on päättynyt tai viestit eivät ole vuokralaiselle käytössä. */
  canReply: z.boolean(),
  messages: z.array(messageSchema),
});
export type Thread = z.infer<typeof threadSchema>;

/** Sivupalkin ja yhteenvedon luvut. */
export const messageCountsSchema = z.object({
  unreadThreads: z.number(),
  openFaults: z.number(),
});
export type MessageCounts = z.infer<typeof messageCountsSchema>;

// ---------------------------------------------------------------------------
// Palaute ylläpidolle (Apua)
// ---------------------------------------------------------------------------

export const feedbackTypes = ['bug', 'feedback', 'question'] as const;
export const feedbackTypeSchema = z.enum(feedbackTypes);
export const feedbackCreateSchema = z.object({
  type: feedbackTypeSchema,
  message: z.string().trim().min(1, { error: 'errors.required' }).max(4000),
  page: z.string().max(300).optional(),
});
export type FeedbackCreate = z.input<typeof feedbackCreateSchema>;

export const feedbackSchema = z.object({
  id: z.uuid(),
  type: feedbackTypeSchema,
  message: z.string(),
  page: z.string().nullable(),
  status: z.enum(['new', 'done']),
  userName: z.string().nullable(),
  userEmail: z.string().nullable(),
  createdAt: z.string(),
});
export type Feedback = z.infer<typeof feedbackSchema>;

/** Tehtävät vuokranantajan yhteenvetoon. */
export const landlordTaskSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('inspection_submitted'),
    propertyId: z.uuid(),
    propertyTitle: z.string(),
    inspectionType: inspectionTypeSchema,
    tenantName: z.string().nullable(),
    at: z.string(),
  }),
  /** Vuokralainen on muuttanut pois, vakuus palauttamatta: sulje vuokrasuhde. */
  z.object({
    type: z.literal('deposit_pending'),
    propertyId: z.uuid(),
    propertyTitle: z.string(),
    leaseId: z.uuid(),
    endDate: z.string(),
    at: z.string(),
  }),
  /** Vuokralainen ilmoitti maksaneensa: vahvista tai hylkää. */
  z.object({
    type: z.literal('invoice_reported'),
    propertyId: z.uuid(),
    propertyTitle: z.string(),
    invoiceId: z.uuid(),
    period: z.string(),
    paidOn: z.string(),
    tenantName: z.string().nullable(),
    at: z.string(),
  }),
]);
export type LandlordTask = z.infer<typeof landlordTaskSchema>;

export const idParamsSchema = z.object({ id: z.uuid() });

export const errorResponseSchema = z.object({
  error: z.string(),
  message: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Vuokralaskut ja maksut
// ---------------------------------------------------------------------------

/** Kuukausi muodossa "2026-10". */
export const monthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, { error: 'errors.month' });

export const invoiceLineSchema = z.object({
  description: z.string().trim().min(1, { error: 'errors.required' }).max(100),
  amountCents: z.number().int().min(0, { error: 'errors.amount' }).max(10_000_000),
});
export type InvoiceLine = z.infer<typeof invoiceLineSchema>;

export const invoiceFormatSchema = z.enum(['app', 'pdf']);
export type InvoiceFormat = z.infer<typeof invoiceFormatSchema>;

export const invoiceTemplateCreateSchema = z
  .object({
    startMonth: monthSchema,
    endMonth: monthSchema.nullable(),
    dueDay: z.number().int().min(1).max(31),
    format: invoiceFormatSchema,
    lines: z.array(invoiceLineSchema).min(1).max(20),
    /** Oma PDF (format = pdf), ladattu ensin Laskut-kansioon. */
    fileId: z.uuid().nullable(),
    /** Tilinumero (format = app). Tallentuu myös käyttäjän tietoihin. */
    iban: ibanSchema.nullable(),
  })
  .refine((v) => v.endMonth === null || v.endMonth >= v.startMonth, {
    error: 'errors.endBeforeStart',
    path: ['endMonth'],
  })
  .refine((v) => v.format !== 'app' || v.iban, { error: 'errors.iban', path: ['iban'] })
  .refine((v) => v.format !== 'pdf' || v.fileId, { error: 'errors.required', path: ['fileId'] });
export type InvoiceTemplateCreate = z.input<typeof invoiceTemplateCreateSchema>;

export const invoiceTemplateSchema = z.object({
  id: z.uuid(),
  leaseId: z.uuid(),
  startMonth: z.string(),
  endMonth: z.string().nullable(),
  dueDay: z.number(),
  format: invoiceFormatSchema,
  lines: z.array(invoiceLineSchema),
  totalCents: z.number(),
  fileId: z.uuid().nullable(),
  fileName: z.string().nullable(),
  iban: z.string().nullable(),
  recipientName: z.string().nullable(),
  /** Voiko pohjan poistaa: viimeisin, eikä sen laskuja ole maksettu tai muokattu. */
  removable: z.boolean(),
  /** Viimeisin pohja: vain sen päättymiskuukautta voi muuttaa. */
  latest: z.boolean(),
  createdAt: z.string(),
});
export type InvoiceTemplate = z.infer<typeof invoiceTemplateSchema>;

/** Pohjan muokkaus: päättymiskuukausi (vain viimeisin pohja) ja eräpäivä. Summat uudella pohjalla. */
export const invoiceTemplateUpdateSchema = z.object({
  endMonth: monthSchema.nullable(),
  dueDay: z.number().int().min(1).max(31),
});
export type InvoiceTemplateUpdate = z.infer<typeof invoiceTemplateUpdateSchema>;

/** reported = vuokralainen ilmoitti maksaneensa, vuokranantaja ei ole vielä vahvistanut. */
export const invoiceStatusSchema = z.enum(['open', 'partial', 'paid', 'overdue', 'reported']);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;

export const invoicePaymentSchema = z.object({
  id: z.uuid(),
  paidOn: z.string(),
  amountCents: z.number(),
});
export type InvoicePayment = z.infer<typeof invoicePaymentSchema>;

export const invoiceSchema = z.object({
  id: z.uuid(),
  leaseId: z.uuid(),
  period: z.string(),
  number: z.number(),
  reference: z.string(),
  dueDate: z.string(),
  lines: z.array(invoiceLineSchema),
  totalCents: z.number(),
  paidCents: z.number(),
  status: invoiceStatusSchema,
  note: z.string().nullable(),
  edited: z.boolean(),
  format: invoiceFormatSchema,
  /** Oma PDF, kun pohja on PDF-muotoinen. */
  fileId: z.uuid().nullable(),
  payments: z.array(invoicePaymentSchema),
  /** Vuokralaisen ilmoittama maksupäivä, odottaa vahvistusta. */
  reportedPaidOn: z.string().nullable(),
  reportedByName: z.string().nullable(),
});
export type Invoice = z.infer<typeof invoiceSchema>;

export const invoiceUpdateSchema = z.object({
  lines: z.array(invoiceLineSchema).min(1).max(20),
  dueDate: leaseDate,
  note: z
    .string()
    .trim()
    .max(500)
    .transform((v) => (v === '' ? null : v))
    .nullable(),
});
export type InvoiceUpdate = z.input<typeof invoiceUpdateSchema>;

export const paymentCreateSchema = z.object({
  paidOn: leaseDate,
  amountCents: z.number().int().min(1, { error: 'errors.amount' }).max(100_000_000),
  /**
   * Ennakkomaksu: maksu kattaa tämän ja seuraavat kuukaudet tähän kuukauteen asti. Silloin
   * summan pitää olla kuukausien avoimien osuuksien summa.
   */
  throughMonth: monthSchema.nullable().optional(),
});
export type PaymentCreate = z.infer<typeof paymentCreateSchema>;

/** Vuokralaisen ilmoitus maksusta. null perii ilmoituksen. */
export const invoiceReportSchema = z.object({ paidOn: leaseDate.nullable() });
export type InvoiceReport = z.infer<typeof invoiceReportSchema>;

// ---------------------------------------------------------------------------
// Kulut
// ---------------------------------------------------------------------------

/** Veroilmoituksen kohtien mukaiset kululajit. */
export const expenseCategories = [
  'maintenance_charge',
  'capital_charge',
  'water',
  'repair',
  'improvement',
  'furniture',
  'interest',
  'insurance',
  'other',
] as const;
export const expenseCategorySchema = z.enum(expenseCategories);
export type ExpenseCategory = z.infer<typeof expenseCategorySchema>;

/** Kululajit, jotka kuuluvat myös huoltokirjaan, ja niitä vastaava merkinnän laji. */
export const MAINTENANCE_KIND_FROM_EXPENSE: Partial<Record<ExpenseCategory, MaintenanceKind>> = {
  repair: 'repair',
  improvement: 'renovation',
  furniture: 'purchase',
};

const expenseDescription = z.string().trim().min(1, { error: 'errors.required' }).max(200);
const expenseAmount = z.number().int().min(1, { error: 'errors.amount' }).max(100_000_000);

export const expenseInputSchema = z
  .object({
    paidOn: leaseDate,
    category: expenseCategorySchema,
    description: expenseDescription,
    amountCents: expenseAmount,
    /** Kuitti, ladattu ensin Kuitit-kansioon. */
    fileId: z.uuid().nullable(),
    /** Huoltokirjan merkintä, josta kulu tehtiin. */
    maintenanceEntryId: z.uuid().nullable(),
    /** Osuus, joka katettiin vuokralaisen vakuudesta (ei vähennyskelpoinen). */
    depositCoveredCents: z.number().int().min(0).max(10_000_000).optional(),
    /** Uusi kulu: luo samalla huoltokirjan merkinnän ja linkittää sen kuluun. */
    addToMaintenance: z.boolean().optional(),
  })
  .refine((v) => (v.depositCoveredCents ?? 0) <= v.amountCents, {
    error: 'errors.depositCovered',
    path: ['depositCoveredCents'],
  });
export type ExpenseInput = z.infer<typeof expenseInputSchema>;

export const expenseSchema = z.object({
  id: z.uuid(),
  paidOn: z.string(),
  category: expenseCategorySchema,
  description: z.string(),
  amountCents: z.number(),
  /** Vakuudesta katettu osuus; verotuksessa vähennetään vain loppu. */
  depositCoveredCents: z.number(),
  fileId: z.uuid().nullable(),
  fileName: z.string().nullable(),
  maintenanceEntryId: z.uuid().nullable(),
  /** Syntyi toistuvasta kulusta. */
  templateId: z.uuid().nullable(),
  edited: z.boolean(),
  createdAt: z.string(),
});
export type Expense = z.infer<typeof expenseSchema>;

export const expenseTotalsSchema = z.object({
  /** Vuokratulot maksupäivän mukaan (vahvistetut maksut), koko summina. */
  incomeCents: z.number(),
  expenseCents: z.number(),
});
export type ExpenseTotals = z.infer<typeof expenseTotalsSchema>;

export const expenseTemplateCreateSchema = z
  .object({
    category: expenseCategorySchema,
    description: expenseDescription,
    amountCents: expenseAmount,
    /** Maksupäivä kuussa. */
    day: z.number().int().min(1).max(31),
    startMonth: monthSchema,
    endMonth: monthSchema.nullable(),
    /** Summan muutos: tämä pohja päättyy uuden alkuun. */
    replacesTemplateId: z.uuid().nullable().optional(),
  })
  .refine((v) => v.endMonth === null || v.endMonth >= v.startMonth, {
    error: 'errors.endBeforeStart',
    path: ['endMonth'],
  });
export type ExpenseTemplateCreate = z.input<typeof expenseTemplateCreateSchema>;

export const expenseTemplateSchema = z.object({
  id: z.uuid(),
  category: expenseCategorySchema,
  description: z.string(),
  amountCents: z.number(),
  day: z.number(),
  startMonth: z.string(),
  endMonth: z.string().nullable(),
  createdAt: z.string(),
});
export type ExpenseTemplate = z.infer<typeof expenseTemplateSchema>;

export const expenseTemplateUpdateSchema = z.object({ endMonth: monthSchema.nullable() });
export type ExpenseTemplateUpdate = z.infer<typeof expenseTemplateUpdateSchema>;

// ---------------------------------------------------------------------------
// Koko talous
// ---------------------------------------------------------------------------

export const financeOverviewSchema = z.object({
  properties: z.array(
    z.object({
      propertyId: z.uuid(),
      title: z.string(),
      ownershipBp: z.number(),
      archived: z.boolean(),
      /** Vahvistetut vuokramaksut maksupäivän mukaan, koko summina. */
      incomeCents: z.number(),
      expenseCents: z.number(),
      /** Erääntyneet maksamattomat laskut (kaikilta vuosilta). */
      overdueCents: z.number(),
    }),
  ),
  overdue: z.array(
    z.object({
      invoiceId: z.uuid(),
      propertyId: z.uuid(),
      propertyTitle: z.string(),
      period: z.string(),
      dueDate: z.string(),
      openCents: z.number(),
    }),
  ),
});
export type FinanceOverview = z.infer<typeof financeOverviewSchema>;

/** Aloitusohjeen askeleet, joita ei voi päätellä asuntolistasta. */
export const onboardingStatusSchema = z.object({
  hasInvoiceTemplate: z.boolean(),
  hasExpense: z.boolean(),
});
export type OnboardingStatus = z.infer<typeof onboardingStatusSchema>;

// ---------------------------------------------------------------------------
// Verot
// ---------------------------------------------------------------------------

/** Verovuoden luvut omistusosuudella, sentteinä. Kohdat 2.1–2.5 = lomake 7H / OmaVero. */
export const taxLinesSchema = z.object({
  /** 2.1 Vuokratulojen määrä koko vuonna (brutto) */
  income: z.number(),
  /** 2.2 Hoitovastikkeet ja vesimaksut */
  maintenanceAndWater: z.number(),
  /** 2.3 Yhtiön tulouttamat pääomavastikkeet */
  capitalCharges: z.number(),
  /** 2.4 Vuosikorjausten kulut */
  repairs: z.number(),
  /** 2.5 Muut kulut = alla olevat osat yhteensä */
  other: z.number(),
  insurance: z.number(),
  otherCosts: z.number(),
  improvementDepreciation: z.number(),
  furnitureImmediate: z.number(),
  furnitureDepreciation: z.number(),
  /** Lainan korot: ilmoitetaan eri kohdassa kuin vuokratulot. */
  interest: z.number(),
  /** Rahastoidut (ei tuloutetut) rahoitusvastikkeet: eivät vähennä, lisätään hankintamenoon. */
  capitalChargesNotDeducted: z.number(),
  /** Poistamatta vuoden lopussa. */
  improvementRemaining: z.number(),
  furnitureRemaining: z.number(),
});
export type TaxLines = z.infer<typeof taxLinesSchema>;

export const taxSummarySchema = z.object({
  year: z.number(),
  properties: z.array(
    z.object({
      propertyId: z.uuid(),
      title: z.string(),
      type: propertyTypeSchema,
      address: z.string(),
      housingCompany: z.string().nullable(),
      housingCompanyBusinessId: z.string().nullable(),
      ownershipBp: z.number(),
      archived: z.boolean(),
      capitalChargeIncome: z.boolean(),
      /** Vuokrattuna ajalla (verovuoden sisällä). Useita erillisiä jaksoja → koko vuosi. */
      rentalPeriod: z.object({ start: z.string(), end: z.string() }).nullable(),
      tenants: z.array(z.string()),
      lines: taxLinesSchema,
      /** Ennen Vuokranttia alkaneiden poistojen alkuarvot on syötetty. */
      hasOpenings: z.boolean(),
      /** Verovuoden remontit, jotka on maksettu ennen vuokrausta tai sen päätyttyä. */
      outsideRental: z.array(
        z.object({
          paidOn: z.string(),
          category: expenseCategorySchema,
          description: z.string(),
          amountCents: z.number(),
          timing: z.enum(['noLease', 'before', 'after']),
        }),
      ),
    }),
  ),
});
export type TaxSummary = z.infer<typeof taxSummarySchema>;

const openingYear = z
  .number({ error: 'errors.year' })
  .int({ error: 'errors.year' })
  .min(1990, { error: 'errors.year' })
  .refine((y) => y <= new Date().getFullYear(), { error: 'errors.year' });

/** Ennen Vuokranttia alkaneet poistot asunnolle, koko asunnon summina. */
export const depreciationOpeningsSchema = z.object({
  /** Aiemmat perusparannukset: alkuperäinen summa ja maksuvuosi. */
  improvements: z
    .array(
      z.object({
        year: openingYear,
        amountCents: expenseAmount,
        description: z.string().trim().max(200),
      }),
    )
    .max(50),
  /** Kalusteiden poistamaton hankintameno vuoden alussa (edellisen vuoden veroilmoituksesta). */
  furnitureBalance: z.object({ year: openingYear, amountCents: expenseAmount }).nullable(),
});
export type DepreciationOpenings = z.infer<typeof depreciationOpeningsSchema>;

export const taxSettingsSchema = z.object({ capitalChargeIncome: z.boolean() });
export type TaxSettings = z.infer<typeof taxSettingsSchema>;

// ---------------------------------------------------------------------------
// Vuokrasuhteiden lokit
// ---------------------------------------------------------------------------

export const logLeaseSchema = z.object({
  leaseId: z.uuid(),
  propertyTitle: z.string(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  status: z.string(),
  tenants: z.array(z.string()),
  /** Vuokralaisen pääsy päättyy (päättyneet vuokrasuhteet). */
  accessUntil: z.string().nullable(),
});
export type LogLease = z.infer<typeof logLeaseSchema>;

export const logListSchema = z.object({
  /** Vuokranantajan asunnot vuokrasuhteineen. */
  owned: z.array(
    z.object({
      propertyId: z.uuid(),
      title: z.string(),
      archived: z.boolean(),
      leases: z.array(logLeaseSchema),
    }),
  ),
  /** Vuokralaisen omat vuokrasuhteet. */
  rented: z.array(logLeaseSchema),
});
export type LogList = z.infer<typeof logListSchema>;

export const leaseLogSchema = z.object({
  propertyTitle: z.string(),
  address: z.string(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  status: z.string(),
  tenants: z.array(z.string()),
  landlords: z.array(z.string()),
  access: z.enum(['owner', 'party']),
  events: z.array(propertyEventSchema),
});
export type LeaseLog = z.infer<typeof leaseLogSchema>;

/** Vuokralaisen historia: päättyneet vuokrasuhteet, joihin pääsy on vielä voimassa. */
export const historyLeaseSchema = z.object({
  leaseId: z.uuid(),
  propertyTitle: z.string(),
  address: z.string(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  tenants: z.array(z.string()),
  landlords: z.array(z.string()),
  depositCents: z.number().int(),
  depositReturnedCents: z.number().int().nullable(),
  depositReturnedOn: z.string().nullable(),
  /** Aineisto on saatavilla tähän päivään asti. */
  accessUntil: z.string().nullable(),
  /** Vuokrasuhteeseen on liitetty sopimustiedosto. */
  hasContract: z.boolean(),
});
export type HistoryLease = z.infer<typeof historyLeaseSchema>;

/** Vuokranantajan historia: asunnot, joilla on päättyneitä vuokrasuhteita, ja arkistoidut. */
export const ownedHistoryLeaseSchema = z.object({
  leaseId: z.uuid(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  tenants: z.array(z.string()),
  terminatedBy: z.enum(['tenant', 'landlord', 'mutual', 'fixed_term']).nullable(),
  rentCents: z.number().int(),
  depositCents: z.number().int(),
  depositReturnedCents: z.number().int().nullable(),
  depositReturnedOn: z.string().nullable(),
  depositNote: z.string().nullable(),
  /** Erääntyneistä laskuista maksamatta jäänyt summa. */
  unpaidCents: z.number().int(),
});
export type OwnedHistoryLease = z.infer<typeof ownedHistoryLeaseSchema>;

export const ownedHistorySchema = z.object({
  properties: z.array(
    z.object({
      propertyId: z.uuid(),
      title: z.string(),
      address: z.string(),
      archived: z.boolean(),
      leases: z.array(ownedHistoryLeaseSchema),
    }),
  ),
});
export type OwnedHistory = z.infer<typeof ownedHistorySchema>;
